import { buildCaselawStandaloneViewerURL } from '@/standalone-reference-viewer/caselaw/utils/build-caselaw-viewer-url'
import { CaselawStorageField } from '@/common/types/caselaw-identifiers'
import { matterPaths } from '@/routing/paths'
import type { CellReference } from './schemas'

/** A viewer target may be a whole source document or an answer's citation. */
export type TableReferenceTarget = Pick<CellReference, 'document_name'> &
  Partial<
    Pick<
      CellReference,
      'id' | 'document_id' | 'page_number' | 'excerpt' | 'web_url' | 'legal_path' | 'legal_kind' | 'agency_table_id' | 'docket_table_id' | 'docket'
    >
  >

/** Preserve the same authorized viewer targets in display and inline editing. */
export function tableReferenceHref(reference: TableReferenceTarget, matterId: string): string | null {
  if (reference.docket_table_id && reference.id) return `${matterPaths.table(matterId, reference.docket_table_id)}?reference=${reference.id}`
  if (reference.agency_table_id && reference.id) return `/dashboard/m/${matterId}/tables/${reference.agency_table_id}?reference=${reference.id}`
  const webHref = reference.web_url && /^https?:\/\//i.test(reference.web_url) ? reference.web_url : null
  const legalHref =
    reference.legal_path && reference.legal_kind
      ? reference.legal_kind === 'lrr'
        ? matterPaths.referenceViewer.lrr(matterId, encodeURIComponent(reference.legal_path))
        : buildCaselawStandaloneViewerURL({ [CaselawStorageField.S3_PATH]: reference.legal_path }, { matterId })
      : null
  if (!reference.document_id && !legalHref && !webHref) return null
  const href =
    webHref ??
    legalHref ??
    `${matterPaths.referenceViewer.userFile(matterId, reference.document_id!)}${reference.page_number ? `?page=${reference.page_number - 1}` : ''}`
  return !webHref && !legalHref && reference.excerpt ? `${href}#${new URLSearchParams({ excerpt: reference.excerpt })}` : href
}

/** Preserve docket currency and source classification wherever a cell citation is labeled. */
export function tableReferenceLabel(reference: TableReferenceTarget): string {
  const parts = [`${reference.document_name}${reference.page_number ? `, page ${reference.page_number}` : ''}`]
  if (reference.docket) {
    const { source } = reference.docket
    if (source.court) parts.push(source.court)
    parts.push(`Retrieved: ${source.retrieved_at.slice(0, 10)}`)
    if (source.provider_last_updated) parts.push(`Docket last updated: ${source.provider_last_updated}`)
    parts.push('Live court record, not legal authority')
  }
  return parts.join(' — ')
}
